/**
 * useAuth.ts — Auth Hook
 *
 * Bridges Firebase Auth (via authService) and the Zustand auth store.
 *
 * Responsibilities:
 *   1. Start listening to Firebase auth state changes on mount.
 *   2. Populate the Zustand auth store with the resolved user.
 *   3. Expose auth actions (signIn, signUp, signOut) to components.
 *
 * Usage:
 *   const { user, isLoading, signIn, signOut } = useAuth();
 *
 * This hook should be used ONCE in App.tsx (or a top-level layout).
 * All other components should read state from useAuthStore() directly —
 * they do NOT need to call this hook, which would create duplicate listeners.
 */
import { useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { authService } from '@/services/auth.service';
import type { User } from 'firebase/auth';

export interface UseAuthReturn {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const { user, isLoading, error, setUser, setError, setLoading } = useAuthStore();

  // ---- Auth state listener -----------------------------------------------

  useEffect(() => {
    // Ensure loading is true while we wait for the first Firebase callback
    setLoading(true);

    const unsubscribe = authService.onAuthStateChanged((firebaseUser) => {
      setUser(firebaseUser);
    });

    // Clean up listener when the component using this hook unmounts
    return unsubscribe;
  }, [setUser, setLoading]);

  // ---- Auth actions -------------------------------------------------------

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      await authService.signIn(email, password);
      // setUser will be called by onAuthStateChanged
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  }, [setLoading, setError]);

  const signUp = useCallback(async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      await authService.signUp(email, password);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  }, [setLoading, setError]);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await authService.signOut();
      // setUser(null) will be called by onAuthStateChanged
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  }, [setError]);

  const sendPasswordResetEmail = useCallback(async (email: string): Promise<void> => {
    try {
      await authService.sendPasswordResetEmail(email);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  }, [setError]);

  return {
    user,
    isLoading,
    error,
    signIn,
    signUp,
    signOut,
    sendPasswordResetEmail,
  };
}
